import { Component, OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChild, Input, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';
import { FormType } from '@omni/classes/dynamic-form/dynamic-form.class';
import { DynamicFormComponentModel, DynamicFormType } from '@omni/models/dynamic-form-component.model';
import { DynamicFormComponent } from '@omni/components/shared/ind-dynamic-form/ind-dynamic-form';
import { MedicalInquiryService } from '../../../services/medical-inquiry/medical-inquiry.service';
import { MedicalInquirySubmitFlowService } from '../../../services/medical-inquiry/medical-inquiry-submit-flow.service';
import { MedicalInquiryDataService } from '@omni/data-services/medical-inquiry/medical-inquiry.data.service';
import { MedicalInquiry, MedicalInquiryStatus } from '@omni/classes/medical-inquiry/medical-inquiry.class';
import { DynamicFormsService } from '../../../services/dynamic-forms/dynamic-forms-service';
import { UIService } from '@omni/services/ui/ui.service';
import { DeviceService } from '@omni/services/device/device.service';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService, ToastStyle } from '@omni/services/notification/notification.service';
import { DateTimeFormatsService } from '@omni/services/date-time-formats/date-time-formats.service';
import { FooterService, FooterViews } from '@omni/services/footer/footer.service';
import { MedicalInquiryFooterRestoreService } from '../../../services/medical-inquiry/medical-inquiry-footer-restore.service';
import { NavigationService, PageName } from '@omni/services/navigation/navigation.service';
import { MedicalInquiryDetailComponent } from '../medical-inquiry-detail/medical-inquiry-detail.component';
import { ActivityService } from '@omni/services/activity/activity.service';
import { MeetingStructureService } from '@omni/services/meeting-structure/meeting-structure.service';
import { IonNav, ModalController } from '@ionic/angular';

@Component({
  selector: 'medical-inquiry-new',
  templateUrl: './medical-inquiry-new.component.html',
  styleUrls: ['./medical-inquiry-new.component.scss'],
  providers: [MedicalInquiryFooterRestoreService],
  // OnPush NOT used here — hosts ind-dynamic-form which mutates inputs internally
})
export class MedicalInquiryNewComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(DynamicFormComponent) dynamicForm: DynamicFormComponent;
  @Input() from: string;
  @Input() modalNavCtrl: IonNav; // IonNav reference from InMeetingCasesComponent for in-modal navigation
  @Input() contact: { ID?: string; fullname?: string }; // the open contact, when created from the contact tool

  formDetail: DynamicFormComponentModel;

  /** True while form definition and option sets are being loaded (offline path). Avoids brief "no form configured" flash. */
  isFormLoading = true;

  /** Used by ind-dynamic-form to load/store linked entity (subgrid) data for medical inquiry */
  readonly medicalInquiryLinkedEntityPrefix = DB_KEY_PREFIXES.MEDICAL_INQUIRY_LINKED_ENTITY;

  /** Tracks created MI id so subgrids stay enabled and we update vs create on subsequent saves */
  private createdMedicalInquiryId: string | null = null;

  /** Pending linked entities (products) to save, same pattern as set-booking-activity-detail */
  private linkEntitiesReqPayload: any[] = [];

  constructor(
    public medicalInquiryService: MedicalInquiryService,
    private readonly medicalInquiryDataService: MedicalInquiryDataService,
    private readonly dynamicFormService: DynamicFormsService,
    private readonly uiService: UIService,
    private readonly device: DeviceService,
    private readonly translate: TranslateService,
    private readonly notificationService: NotificationService,
    private readonly medicalInquirySubmitFlowService: MedicalInquirySubmitFlowService,
    private readonly footerService: FooterService,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly footerRestoreService: MedicalInquiryFooterRestoreService,
    private readonly navService: NavigationService,
    private readonly activityService: ActivityService,
    private readonly meetingStructureService: MeetingStructureService,
    private readonly modalCtrl: ModalController,
    private readonly cdr: ChangeDetectorRef,
    private readonly dateTimeFormatsService: DateTimeFormatsService,
  ) {}

  /** Both in-meeting entry points: the modal (meeting detail) and the presentation child nav. */
  private get isInMeetingContext(): boolean {
    return this.from === 'InMeetingModal' || this.from === 'InMeetingPresentation';
  }

  ngOnInit() {
    this.medicalInquiryService.setFormDirty(false);
    void this.loadCreateForm();
  }

  /** Fires when component is a real Ionic page (e.g. pushed from meeting). */
  ionViewWillEnter(): void {
    this.footerRestoreService.restoreNow();
  }

  ngAfterViewInit(): void {
    this.footerRestoreService.startObserving(
      this.elementRef.nativeElement,
      () => this._restoreFooterWhenViewReentered()
    );
  }

  ngOnDestroy(): void {
    this.footerRestoreService.destroy();
    if (this.from === 'InMeetingModal' || this.from === 'InMeetingPresentation') {
      this.medicalInquiryService.isCreatingFromMeeting = false;
    }
  }

  /** Restore footer and Submit state when view is re-entered (e.g. after navigating to contact and back). */
  private _restoreFooterWhenViewReentered(): void {
    if (!this.formDetail?.footerView) return;
    this.footerService.initButtons(FooterViews.MedicalInquiryNew);
    if (this.createdMedicalInquiryId) {
      this.formDetail.footerSubmitUnlocked = true;
    }
    this.dynamicForm?.refreshFormStateAndButtons();
  }

  private async loadCreateForm(): Promise<void> {
    try {
      const dynamicForm = this.dynamicFormService.getFormDefinitionForEntity('indskr_medicalinquiry', FormType.CREATEFORM);
      if (!dynamicForm) {
        this.formDetail = null;
        return;
      }

      if (this.device.isOffline) {
        const cachedOptions = await this.dynamicFormService.getMIFormOptionSetsFromDb();
        this.dynamicFormService.hydrateMedicalInquiryFormOptionSets(dynamicForm, cachedOptions);
      }

      const formEntityObject: Record<string, unknown> = {};
      formEntityObject['indskr_reporteddate'] = this.dateTimeFormatsService.toISOString(new Date());
      // Show "Medical Inquiry" as a placeholder label when the title field is empty.
      // This is stripped back to '' before any Dynamics call so the auto-generate plugin still fires.
      formEntityObject['indskr_title'] = this.translate.instant('MEDICAL_INQUIRY');

      // Carry the meeting's customer over only when it is unambiguous. A multi-contact meeting is
      // left blank deliberately - guessing one would file the inquiry against the wrong HCP.
      // Mirrors createInMeetingInquiry's rule for Customer Inquiry.
      if (this.isInMeetingContext) {
        const meetingContacts = this.activityService.selectedActivity?.['contacts'];
        if (meetingContacts?.length === 1 && meetingContacts[0]?.ID) {
          formEntityObject['_indskr_contact_value'] = meetingContacts[0].ID;
          formEntityObject['_indskr_contact_value@OData.Community.Display.V1.FormattedValue'] = meetingContacts[0].fullname || '';
        }
      } else if (this.from === 'ContactTool' && this.contact?.ID) {
        // Created from a contact's own page, so the customer is unambiguous.
        formEntityObject['_indskr_contact_value'] = this.contact.ID;
        formEntityObject['_indskr_contact_value@OData.Community.Display.V1.FormattedValue'] = this.contact.fullname || '';
      }

      const formDetail: DynamicFormComponentModel = {
        title: this.translate.instant('MEDICAL_INQUIRY'),
        LookupEntitySetName: 'indskr_medicalinquiries',
        showLeftHeaderButton: this.device.isMobileDevice,
        leftHeaderBtnIcon: 'chevron-back-outline',
        showRightHeaderButton: true,
        rightHeaderImgSrc: 'assets/imgs/header_save.svg',
        rightHeaderBtnText: this.translate.instant('SAVE'),
        formMetadata: dynamicForm,
        formEntityObject,
        mainEntityObjectId: null,
        dynamicFormType: DynamicFormType.CONFIGUREDFORM,
        navOptions: { animate: false },
        eventsHandler: (data: unknown, eventTarget: string) => this.handleFormEvent(data, eventTarget),
        footerView: 'MedicalInquiryNew',
        footerViewManagedByParent: true,
        // Presentation renders its own footer for the embedded activity. Safe to drop ours because
        // the form swaps its header to Save + Submit once the record exists (NEWDATACREATED), and
        // there is nothing to submit before that.
        hideFooterToolbar: this.from === 'InMeetingPresentation',
        loadAddressFromLocal: true,
        modalNavCtrl: this.modalNavCtrl || undefined,
      };

      this.formDetail = formDetail;
    } finally {
      this.isFormLoading = false;
    }
  }

  private async handleFormEvent(data: any, eventTarget: string): Promise<void> {
    if (!eventTarget) return;
    switch (eventTarget) {
      case 'DONEBUTTONCLICK':
        await this.handleSaveOrCreate(data);
        break;
      case 'SAVEMEDICALINQUIRY':
        await this.handleUpdate(data);
        break;
      case 'SUBMITMEDICALINQUIRY':
        await this.handleSubmitMedicalInquiry(data);
        break;
      case 'CANCELBUTTONCLICK': {
        const payload = data ? { ...data, isDirty: data.displayConfirmation ?? data.isDirty } : undefined;
        const isDirtyAtCancel = this.createdMedicalInquiryId != null
          ? (data?.displayConfirmation ?? data?.isDirty ?? false)
          : (payload ? this._isCreateFormActuallyDirty(payload) : this.medicalInquiryService.isFormDirty);
        this.medicalInquiryService.setFormDirty(!!isDirtyAtCancel);
        if (isDirtyAtCancel && !(await this.medicalInquiryService.confirmDiscardIfDirty())) {
          return;
        }

        if (this.createdMedicalInquiryId) {
          this.navigateToDisplayForm();
        } else {
          this.medicalInquiryService.setFormDirty(false);
          this.medicalInquiryService.setSelectedMI(null);
          this.medicalInquiryService.setShowNew(false);
          this._dismissCreateForm();
        }
        break;
      }
      case 'ADDEDLINKEDENTITY':
      case 'SCRAPLINKEDENTITY':
        if (data?.entity && data?.values) {
          MedicalInquiryService.upsertLinkEntityPayload(this.linkEntitiesReqPayload, data);
        }
        break;
      case 'ISFORMDIRTY':
        const isDirty = this.createdMedicalInquiryId != null
          ? (data?.isDirty ?? false)
          : (this._isCreateFormActuallyDirty(data) ?? false);
        this.medicalInquiryService.setFormDirty(isDirty);
        this._updateFormDetailTitleFromFormValue(data);
        break;
      default:
        break;
    }
  }

  /** Only true when form reports dirty and user has added data (non-empty string or linked entities). Aligns with sample reconciliation hasUnsavedCreateModeChanges / kit booking: no discard when no data entered. */
  private _isCreateFormActuallyDirty(data: any): boolean {
    if (!data?.isDirty) return false;
    if (this.linkEntitiesReqPayload?.length > 0) return true;
    const formValue = data?.formValue ?? data?.formStringValue ?? {};
    if (typeof formValue !== 'object' || formValue === null) return false;
    for (const key of Object.keys(formValue)) {
      if (key === 'indskr_reporteddate') continue;
      if (this._hasMeaningfulUserValue(formValue[key])) return true;
    }
    return false;
  }

  /** True when value indicates user input: non-empty string, boolean (checkbox/yes-no), lookup selection (object with id or non-empty lookupfields array), or non-empty array. Ignores numbers so default picklist option-set values do not trigger discard. */
  private _hasMeaningfulUserValue(value: unknown): boolean {
    if (value === undefined || value === null) return false;
    if (typeof value === 'string') return value.trim() !== '';
    if (typeof value === 'boolean') return true;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      if (obj.id !== undefined && obj.id !== null && obj.id !== '') return true;
      const lookupfields = obj.lookupfields;
      if (Array.isArray(lookupfields) && lookupfields.length > 0) return true;
    }
    return false;
  }

  /**
   * Strip the "Medical Inquiry" placeholder from a save payload before it reaches Dynamics.
   * Sending '' (empty string) lets the auto-generate plugin produce a real title.
   * A value the user typed themselves will differ from the placeholder and is left untouched.
   */
  private _stripTitlePlaceholder(payload: Record<string, unknown>): void {
    if (payload['indskr_title'] === this.translate.instant('MEDICAL_INQUIRY')) {
      payload['indskr_title'] = '';
    }
  }

  /** When form is dirty and includes indskr_title, use it as DynamicFormComponentModel title. */
  private _updateFormDetailTitleFromFormValue(data: any): void {
    if (!this.formDetail) return;
    if (typeof data !== 'object' || data === null) return;
    const titleValue = data?.formStringValue?.indskr_title ?? data?.formValue?.indskr_title;
    const trimmed = typeof titleValue === 'string' ? titleValue.trim() : '';
    this.formDetail.title = trimmed || this.translate.instant('MEDICAL_INQUIRY');
  }

  /** Set header title from created/response entity (indskr_title from API or MedicalInquiry.title). */
  private _setFormDetailTitleFromEntity(entity: { indskr_title?: string; title?: string } | null | undefined): void {
    if (!this.formDetail) return;
    const titleValue = entity?.indskr_title ?? entity?.title ?? '';
    const trimmed = typeof titleValue === 'string' ? titleValue.trim() : '';
    this.formDetail.title = trimmed || this.translate.instant('MEDICAL_INQUIRY');
  }

  /** First save: create record, stay on form, enable subgrids (similar to kit booking). */
  private async handleSaveOrCreate(data: any): Promise<void> {
    const payload = data?.formValue || data;
    if (!payload || (typeof payload === 'object' && Object.keys(payload).length === 0)) {
      return;
    }
    this.linkEntitiesReqPayload = [];
    await this.uiService.displayLoader();

    // When creating inside a meeting, bind the appointment lookup so the MI tile
    // persists in the meeting structure on reload (requires indskr_appointment on Dynamics).
    const isInMeeting = this.isInMeetingContext;
    const meetingId = isInMeeting ? (this.activityService.selectedActivity?.ID || '') : '';
    const isOnlineMeetingId = meetingId && !meetingId.includes('offline');
    const enrichedPayload = isOnlineMeetingId
      ? { ...payload, 'indskr_Appointment@odata.bind': `/appointments(${meetingId})` }
      : { ...payload };
    // Strip the "Medical Inquiry" placeholder before sending so auto-generate plugin fires on empty title.
    this._stripTitlePlaceholder(enrichedPayload);

    try {
      if (this.device.isOffline) {
        // Pass formStringValue so the data service can mirror the contact display name into the
        // read-side OData formatted-value key on rawData. Without this the offline-created MI has
        // empty `contactFormatted`, which hides the "Signed By: <name>, <date>" line in the
        // capture-signatures modal at submit time and leaves the LHS card / agenda Customer empty
        // (OMNI-59455 Observation 6 + Obs 3 sibling).
        const localMi = this.medicalInquiryDataService.createMedicalInquiryOffline(
          enrichedPayload,
          data?.formStringValue as Record<string, string> | undefined
        );
        if (meetingId) {
          localMi.appointmentId = meetingId;
          (localMi.rawData as Record<string, unknown>)['_indskr_appointment_value'] = meetingId;
          this.medicalInquiryDataService.upsertMedicalInquiry(localMi);
        }
        const id = localMi.id;
        if (data?.eventsHandler) {
          // Use the placeholder when both title and rawData are empty (stripped for offline auto-generate),
          // so the form field keeps showing a readable label instead of going blank.
          const resolvedTitle = localMi.title
            || (localMi.rawData as Record<string, unknown>)?.['indskr_title'] as string
            || this.translate.instant('MEDICAL_INQUIRY');
          const createdPayload = { indskr_medicalinquiryid: id, indskr_title: resolvedTitle, indskr_id: (localMi.rawData as Record<string, unknown>)?.['indskr_id'] as string ?? '' };
          data.eventsHandler(createdPayload, 'NEWDATACREATED');
        }
        this.createdMedicalInquiryId = id;
        this.formDetail.footerSubmitUnlocked = true;
        this._setFormDetailTitleFromEntity(localMi);
        this.dynamicForm?.refreshFormStateAndButtons();
        this.medicalInquiryService.setFormDirty(false);
        this.medicalInquiryService.setSelectedMI(localMi);
        if (this.from === 'NewActivity' || this.from === 'ContactTool') this.activityService.selectedActivity = localMi;
        // Add MI to agenda as an Activity
        await this.activityService.addActivity(localMi);
        this.activityService.publishActivityEvent({ action: 'Create', activity: localMi });
        this.activityService.finalizeAgendaRefresh();
        // Add MI tile to Meeting Structure if created in-meeting
        if (isInMeeting) {
          this.meetingStructureService.createInMeetingEmbededActivity(localMi);
        }
        this.notificationService.notify(
          this.translate.instant('SAVED_OFFLINE_WILL_SYNC') || 'Saved offline. Will sync when connected.',
          'MedicalInquiryNewComponent',
          'top',
          ToastStyle.INFO,
          3000
        );
        this.cdr.markForCheck();
      } else {
        const res = await this.medicalInquiryDataService.createMedicalInquiry(enrichedPayload);
        const idRaw = res?.indskr_medicalinquiryid ?? res?.guid ?? res;
        const id = typeof idRaw === 'string' ? idRaw : idRaw != null ? String(idRaw) : '';
        if (id && data?.eventsHandler) {
          const created = new MedicalInquiry(res as Record<string, unknown>);
          // Ensure appointmentId is set even if Dynamics didn't return _indskr_appointment_value
          if (meetingId && !created.appointmentId) created.appointmentId = meetingId;
          const createdPayload = { indskr_medicalinquiryid: id, indskr_title: created.title || (res as Record<string, unknown>)?.['indskr_title'] as string, indskr_id: (created.rawData as Record<string, unknown>)?.['indskr_id'] as string ?? (res as Record<string, unknown>)?.['indskr_id'] as string ?? '' };
          data.eventsHandler(createdPayload, 'NEWDATACREATED');
          this.createdMedicalInquiryId = id;
          this.formDetail.footerSubmitUnlocked = true;
          this._setFormDetailTitleFromEntity(created);
          this.dynamicForm?.refreshFormStateAndButtons();
          this.medicalInquiryService.setFormDirty(false);
          this.medicalInquiryDataService.upsertMedicalInquiry(created);
          this.medicalInquiryService.setSelectedMI(created);
          // Home's list highlights selectedActivity; in a meeting it holds the meeting, so only from New Activity
          if (this.from === 'NewActivity' || this.from === 'ContactTool') this.activityService.selectedActivity = created;
          // Add MI to agenda as an Activity
          await this.activityService.addActivity(created);
          this.activityService.publishActivityEvent({ action: 'Create', activity: created });
          this.activityService.finalizeAgendaRefresh();
          // Add MI tile to Meeting Structure if created in-meeting
          if (isInMeeting) {
            this.meetingStructureService.createInMeetingEmbededActivity(created);
          }
          this.notificationService.notify(this.translate.instant('UPDATED_SUCCESS'), 'MedicalInquiryNewComponent', 'top', ToastStyle.INFO, 3000);
          this.cdr.markForCheck();
        }
      }
    } catch (err) {
      console.error('MedicalInquiryNewComponent: create failed', err);
      this.notificationService.notify(this.translate.instant('FAILED'), 'MedicalInquiryNewComponent', 'top', ToastStyle.DANGER, 3000);
    } finally {
      await this.uiService.dismissLoader();
    }
  }

  /** Update existing MI (after first save, when subgrids are enabled). Includes linked entities (products).
   * Online, product scraps, adds and the MI update go in one $batch (saveMedicalInquiryWithProducts). */
  private async handleUpdate(data: any, options?: { successToastKey?: string; onSuccess?: () => void }): Promise<void> {
    if (!this.createdMedicalInquiryId) return;
    const payload = data?.formValue || data;
    if (!payload || (typeof payload === 'object' && Object.keys(payload).length === 0)) {
      return;
    }
    // Strip placeholder so it is never persisted or sent to Dynamics as-is.
    this._stripTitlePlaceholder(payload);
    await this.uiService.displayLoader();
    try {
      if (this.device.isOffline) {
        await this._flushPendingLinkedEntitiesOffline(this.createdMedicalInquiryId, data?.eventsHandler);
        this.medicalInquiryDataService.updateMedicalInquiryOffline(this.createdMedicalInquiryId, payload);
        const updated = this.medicalInquiryDataService.medicalInquiries.find(m => m.id === this.createdMedicalInquiryId);
        if (updated) this.medicalInquiryService.syncMedicalInquiryToAgenda(updated);
        if (data?.eventsHandler) {
          data.eventsHandler({ indskr_medicalinquiryid: this.createdMedicalInquiryId, indskr_id: (updated?.rawData as Record<string, unknown>)?.['indskr_id'] as string ?? '' }, 'NEWDATAUPDATED');
        }
        this.notificationService.notify(
          this.translate.instant('SAVED_OFFLINE') || 'Saved offline.',
          'MedicalInquiryNewComponent',
          'top',
          ToastStyle.INFO,
          3000
        );
      } else {
        const { miResult, addedProducts } = await this.medicalInquiryDataService.saveMedicalInquiryWithProducts(
          this.createdMedicalInquiryId,
          this.linkEntitiesReqPayload,
          payload
        );
        this.linkEntitiesReqPayload = [];
        addedProducts
          .filter(({ change }) => change.addedNewDataId)
          .forEach(({ change, guid }) => data?.eventsHandler?.({ addedNewDataId: change.addedNewDataId, linkedEntityId: guid }, 'LINKEDENTITYIDUPDATE'));
        if (miResult instanceof Error) throw miResult;
        const response = miResult;
        if (response) {
          let updatedMi = this.medicalInquiryDataService.upsertMedicalInquiryFromApiResponse(response);
          // Always force-refresh from server to pick up server-generated fields
          // (indskr_id auto-number, indskr_title with product name from post-operation plugins).
          {
            // getMedicalInquiryById already applies _patchTitleFromProduct before storing.
            const refreshed = await this.medicalInquiryDataService.getMedicalInquiryById(this.createdMedicalInquiryId, true);
            if (refreshed) {
              this.medicalInquiryDataService.upsertMedicalInquiry(refreshed);
              updatedMi = refreshed;
            }
          }
          if (updatedMi) this.medicalInquiryService.syncMedicalInquiryToAgenda(updatedMi);
          this._setFormDetailTitleFromEntity(updatedMi);
          if (data?.eventsHandler) {
            data.eventsHandler(
              { indskr_medicalinquiryid: this.createdMedicalInquiryId, indskr_title: updatedMi?.title ?? (response as Record<string, unknown>)?.['indskr_title'] ?? '', indskr_id: (updatedMi?.rawData as Record<string, unknown>)?.['indskr_id'] as string ?? '' },
              'NEWDATAUPDATED'
            );
          }
        } else {
          // Dynamics returned 204: patch in-memory MI so LHS list reflects the change without waiting for next sync
          const existing = this.medicalInquiryDataService.medicalInquiries.find(m => m.id === this.createdMedicalInquiryId);
          if (existing) {
            const raw = { ...(existing.rawData as Record<string, unknown>), ...payload };
            const updatedMi = new MedicalInquiry(raw);
            this.medicalInquiryDataService.upsertMedicalInquiry(updatedMi);
            this.medicalInquiryService.syncMedicalInquiryToAgenda(updatedMi);
            this._setFormDetailTitleFromEntity(updatedMi);
          }
          if (data?.eventsHandler) {
            data.eventsHandler({ indskr_medicalinquiryid: this.createdMedicalInquiryId, indskr_id: (existing?.rawData as Record<string, unknown>)?.['indskr_id'] as string ?? '' }, 'NEWDATAUPDATED');
          }
        }
        const toastKey = options?.successToastKey ?? 'UPDATED_SUCCESS';
        this.notificationService.notify(this.translate.instant(toastKey), 'MedicalInquiryNewComponent', 'top', ToastStyle.INFO, 3000);
      }
      this.medicalInquiryService.setFormDirty(false);
      options?.onSuccess?.();
    } catch (err) {
      console.error('MedicalInquiryNewComponent: update failed', err);
      this.notificationService.notify(this.translate.instant('FAILED'), 'MedicalInquiryNewComponent', 'top', ToastStyle.DANGER, 3000);
      if (data?.eventsHandler) {
        data.eventsHandler('ERROR', 'NEWDATAUPDATED');
      }
    } finally {
      await this.uiService.dismissLoader();
    }
  }

  /**
   * Leave the never-saved create form. The modal dismisses itself; every other host pushed this
   * page onto its own child nav, so it pops. One place, because three separate copies of this
   * branch list drifted and left Back dead for the hosts that were added later.
   */
  private _dismissCreateForm(): void {
    if (this.from === 'InMeetingModal') {
      this.modalCtrl.dismiss();
    } else {
      this.navService.popChildNavPageWithPageTracking();
    }
  }

  /** Close form and navigate to list (clear selection so right pane shows list, not detail). */
  private handleDone(data: any): void {
    this.medicalInquiryService.setFormDirty(false);
    this.medicalInquiryService.setSelectedMI(null);
    this.medicalInquiryService.setShowNew(false);
    this._dismissCreateForm();
  }

  private navigateToDisplayForm(): void {
    const submittedMi = this.medicalInquiryDataService.medicalInquiries.find(
      m => m.id === this.createdMedicalInquiryId
    );

    this.medicalInquiryService.setFormDirty(false);
    this.medicalInquiryService.setShowNew(false);

    if (submittedMi) {
      this.medicalInquiryService.setSelectedMI(submittedMi);
    }

    if (!submittedMi) {
      this.medicalInquiryService.setSelectedMI(null);
      this._dismissCreateForm();
      return;
    }

    if (this.from === 'InMeetingModal') {
      if (this.modalNavCtrl) {
        this.modalNavCtrl.setRoot(MedicalInquiryDetailComponent, {
          from: 'InMeetingModal',
          modalNavCtrl: this.modalNavCtrl,
        });
      } else {
        this.modalCtrl.dismiss();
      }
    } else if (this.from === 'NewActivity') {
      this.navService.setChildNavRoot(
        MedicalInquiryDetailComponent,
        PageName.MedicalInquiryDetailComponent,
        PageName.GoAppActivitiesPageComponent,
        { from: 'AgendaView', activity: submittedMi }
      );
    } else if (this.from === 'InMeetingPresentation') {
      this.navService.setChildNavRoot(
        MedicalInquiryDetailComponent,
        PageName.MedicalInquiryDetailComponent,
        PageName.PresentationMeetingComponent,
        { from: 'InMeetingPresentation', activity: submittedMi }
      );
    } else if (this.from === 'ContactTool') {
      // Replace this create form with the detail rather than re-rooting the child nav: the
      // contact's own pane sits underneath and must survive, so Back has somewhere to pop to.
      // Re-rooting left the detail as the only page and Back rendered an empty pane.
      void this.navService.popChildNavPageWithPageTracking().then(() =>
        this.navService.pushChildNavPageWithPageTracking(
          MedicalInquiryDetailComponent,
          PageName.MedicalInquiryDetailComponent,
          PageName.ContactPageComponent,
          { from: 'ContactTool', activity: submittedMi }
        )
      );
    }
  }

  private async _flushPendingLinkedEntitiesOffline(
    miId: string,
    eventsHandler?: (data: any, eventName: string) => void
  ): Promise<void> {
    if (!this.linkEntitiesReqPayload?.length) return;
    const addPayloads = this.linkEntitiesReqPayload.filter(p => !(p.values?.statecode === 1));
    for (const p of addPayloads) {
      if (p.linkedEntityId || (p.values as any)?.guid) continue;
      const tempId = await this.medicalInquiryDataService.addProductOffline(miId, p.values, p.formStringValue);
      if (eventsHandler && tempId && p.addedNewDataId) {
        eventsHandler({ addedNewDataId: p.addedNewDataId, linkedEntityId: tempId }, 'LINKEDENTITYIDUPDATE');
      }
    }
    this.linkEntitiesReqPayload = [];
  }

  /** Submit MI: confirmation, dual signature modal, then update with submitted status and close (same as detail). */
  private async handleSubmitMedicalInquiry(data: any): Promise<void> {
    if (!this.createdMedicalInquiryId) return;

    const createdMi = this.medicalInquiryDataService.medicalInquiries.find(m => m.id === this.createdMedicalInquiryId);
    const signatureResult = await this.medicalInquirySubmitFlowService.requestSubmitWithSignatures({
      hcpContactName: createdMi?.contactFormatted ?? '',
    });
    if (!signatureResult) return;

    if (this.device.isOffline) {
      await this.uiService.displayLoader();
      try {
        await this._flushPendingLinkedEntitiesOffline(this.createdMedicalInquiryId, data?.eventsHandler);
        const formPayload = data?.formValue || data;
        if (formPayload && typeof formPayload === 'object' && Object.keys(formPayload).length > 0) {
          this._stripTitlePlaceholder(formPayload as Record<string, unknown>);
          this.medicalInquiryDataService.updateMedicalInquiryOffline(this.createdMedicalInquiryId, formPayload);
        }
        this.medicalInquiryDataService.submitMedicalInquiryOffline(
          this.createdMedicalInquiryId,
          signatureResult.hcpSignature,
          signatureResult.repSignature,
          signatureResult.hcpSignatureDateRaw,
          signatureResult.repSignatureDateRaw,
          signatureResult.hcpSignatureTerms
        );
        const updated = this.medicalInquiryDataService.medicalInquiries.find(
          m => m.id === this.createdMedicalInquiryId
        );
        if (updated) {
          this.medicalInquiryService.setSelectedMI(updated);
          this.medicalInquiryService.syncMedicalInquiryToAgenda(updated);
        }
        this.notificationService.notify(
          this.translate.instant('SUBMITTED_OFFLINE_WILL_SYNC') || 'Submitted offline. Will sync when connected.',
          'MedicalInquiryNewComponent',
          'top',
          ToastStyle.INFO,
          3000
        );
        this.navigateToDisplayForm();
      } finally {
        this.uiService.dismissLoader();
      }
      return;
    }

    const formPayload = data?.formValue ?? data ?? {};
    const submitPayload = typeof formPayload === 'object' && formPayload !== null
      ? {
          ...formPayload,
          statuscode: MedicalInquiryStatus.SUBMITTED,
          indskr_hcpsignature: signatureResult.hcpSignature,
          indskr_repsignature: signatureResult.repSignature,
          ...(signatureResult.hcpSignatureDateRaw ? { indskr_hcpsignaturedate: signatureResult.hcpSignatureDateRaw } : {}),
          ...(signatureResult.repSignatureDateRaw ? { indskr_usersignaturedate: signatureResult.repSignatureDateRaw } : {}),
          ...(signatureResult.hcpSignatureTerms ? { indskr_hcpsignatureterms: signatureResult.hcpSignatureTerms } : {}),
        }
      : {
          statuscode: MedicalInquiryStatus.SUBMITTED,
          indskr_hcpsignature: signatureResult.hcpSignature,
          indskr_repsignature: signatureResult.repSignature,
          ...(signatureResult.hcpSignatureDateRaw ? { indskr_hcpsignaturedate: signatureResult.hcpSignatureDateRaw } : {}),
          ...(signatureResult.repSignatureDateRaw ? { indskr_usersignaturedate: signatureResult.repSignatureDateRaw } : {}),
          ...(signatureResult.hcpSignatureTerms ? { indskr_hcpsignatureterms: signatureResult.hcpSignatureTerms } : {}),
        };
    await this.handleUpdate(
      { ...data, formValue: submitPayload },
      { successToastKey: 'MEDICAL_INQUIRY_SUBMITTED_SUCCESS', onSuccess: () => this.navigateToDisplayForm() }
    );
  }
}
